/**
 * Classifiers for transient Medplum failures. Medplum and Postgres report these as prose (in a
 * response body or in a thrown fetch error), so the substring checks live here once.
 */

/**
 * Checks if response body text contains PostgreSQL serialization/deadlock errors.
 * These are transient database errors that are safe to retry.
 */
export function containsPostgresSerializationError(text: string): boolean {
  const normalized = text.toLowerCase();
  return (
    normalized.includes("could not serialize access") ||
    normalized.includes("serialization failure") ||
    normalized.includes("deadlock detected") ||
    normalized.includes("canceling statement due to lock timeout")
  );
}

/**
 * Checks if response body text contains generic transient error indicators.
 */
export function containsTransientErrorMessage(text: string): boolean {
  const normalized = text.toLowerCase();
  return (
    normalized.includes("rate limit") ||
    normalized.includes("too many request") ||
    normalized.includes("timeout")
  );
}

/**
 * Transient connection-level failures: fetch threw before any HTTP response —
 * DNS hiccup, refused/reset socket, dropped connection. These are almost always
 * recoverable on retry; a brief network blip should not abort a long-running job
 * (e.g. a large dataset approval). Matches the Medplum SDK's "Unable to connect"
 * message plus the common Node/undici socket error codes.
 */
function containsConnectionError(text: string): boolean {
  const normalized = text.toLowerCase();
  return (
    normalized.includes("unable to connect") || // Medplum SDK fetch-failure message
    normalized.includes("network error") || // generic "request failed with network error"
    normalized.includes("fetch failed") || // undici/Node fetch connection failure
    normalized.includes("socket hang up") ||
    normalized.includes("econnrefused") ||
    normalized.includes("econnreset") ||
    normalized.includes("etimedout") ||
    normalized.includes("enotfound") ||
    normalized.includes("eai_again") // transient DNS failure
  );
}

/**
 * True when an error thrown before any HTTP response (fetch, socket, DNS) looks transient:
 * a Postgres serialization failure, a rate limit or timeout, or a connection failure.
 * Callers must rule out a deliberate abort first, because "aborted" reads as a connection error.
 */
export function isTransientMedplumNetworkError(error: unknown): boolean {
  const message =
    error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();

  return (
    containsPostgresSerializationError(message) ||
    containsTransientErrorMessage(message) ||
    containsConnectionError(message)
  );
}
